import type { FormValidationResult } from '@repo/commons/models'
import type { ActivityViewModel, FlowViewModel, TestSuiteViewModel } from '@repo/commons/models'
import type { CustomTypeSchema } from '../persistence/custom-type-schema.ts'
import type {
	Topic,
	AbstractReportingElement,
	StateType,
	ReportingElementType as ReportingType,
	PrequalificationState as PersistencePrequalificationState,
	FaqState as PersistenceFaqState,
	SlotFillingState as PersistenceSlotFillingState,
	ReadState as PersistenceReadState,
	// ExpertiseState as PersistenceExpertiseState,
	QueryState as PersistenceQueryState,
	PromptState as PersistencePromptState,
	FunctionCallState as PersistenceFunctionCallState,
	// SubFlowState as PersistenceSubFlowState,
	StartState as PersistenceStartState,
	EndState as PersistenceEndState,
	Usecase,
	Constraint,
	FunctionContract,
	AdapterService,
	AdapterException,
	ResourceSchema,
	ProjectParameter,
	AbstractResource,
	InstanceDescription,
	Lever,
	InstanceConfiguration
} from '@semanticedge/persistence-service-noheaders-client/src'
import type {
	Persona,
	IntentSpectrum,
	KnowledgeCollectionDTO,
	Tag
} from '@semanticedge/knowledge-service-noheaders-client/src'
import type { NluConfig } from '@repo/commons/models'
import type { Node, Edge } from '@xyflow/svelte'
import type { IconName } from '$lib_commons/svg-icons/iconsMap.ts'
import type { FlowEvent } from './flow-event.ts'

/*
These are elements that are relevant only visually, and as such appear in the `flowUi` part of the 
persistence project object as opposed to `flowDefinition`.
*/
export const UINodeTypes = {
	EDGE_ANCHOR_NODE: 'EDGE_ANCHOR_NODE',
	COMMENT_NODE: 'COMMENT_NODE'
} as const

export type UINodeType = (typeof UINodeTypes)[keyof typeof UINodeTypes]

/*
A node in Flow.svelte can belong to one of the following groups.
*/
export type NodeType = StateType | ReportingType | UINodeType

export type NamedElement =
	| ActivityViewModel
	| FlowViewModel
	| Topic
	| AbstractReportingElement
	| CustomTypeSchema
	| IdAndNamedElement
	| Constraint
	| FunctionContract
	| AdapterService
	| AdapterException
	| ResourceSchema
	| AbstractResource
	| InstanceDescription
	| InstanceConfiguration

// when not an entire Element but rather only the id needs to be sent for an ElementOperation (e.g. delete by id)
export type Element = NamedElement | Node | Edge | FlowEvents

/*
These should have the OuptutObjectForm.svelte rendered within their SideSheet forms and should also add
the relevant OutputObject '@class' attribute when mapping to persistence (so that persistence can
correctly serialize the project object properly.)
*/
export type StateWithOutputObject =
	| PersistenceQueryState
	| PersistencePromptState
	| PersistencePrequalificationState

/*
These states should ensure to add the relevant '@class' attribute when mapping to persistence (so that
persistence can correctly serialize the project object properly.)
*/
export type StateWithNluConfig = PersistenceQueryState

/*
To differentiate between different elements that are directly CRUD-able from the SvelteFlow component
*/
export enum GraphElementType {
	NODE = 'NODE',
	EDGE = 'EDGE'
}

export type SvelteFlowGraphElement = Node | Edge

/*
We show one of the following two palettes, depending on user selection.
*/
export enum PaletteType {
	STATES = 'States',
	REPORTING = 'Reporting'
}

/*
This type is used to catch both signatures of the onedgecontextmenu and onnodecontextmenu events,
fired by SvelteFlow.
*/
export type GraphContextMenuArgs =
	| { node: Node; event: MouseEvent }
	| { edge: Edge; event: MouseEvent }

/*
The nodes are divided into various groups in the palette.
*/
export enum PaletteVariant {
	BASIC = 'Basic',
	MAIN = 'Main',
	LLM = 'Llm',
	REPORTING = 'Reporting'
}

/*
SvelteFlow can render different Svelte components as edges based on the edge.type attribute on
the SvelteFlowEdge model. Currently all edges are rendered as SmoothStepEdge.svelte.
*/
export enum EdgeType {
	SMOOTH_STEP = 'SMOOTH_STEP'
}

/*
Even though all edges are functinonally SmoothStepEdge.svelte, there are visual variations.
A given edge in the Flow can be one of these three variants.
*/
export enum EdgeVariant {
	DEFAULT_TRANSITION = 'DEFAULT_TRANSITION',
	NON_DEFAULT_TRANSITION = 'NON_DEFAULT_TRANSITION',
	REPORTING = 'REPORTING'
}

export interface EdgeVariantProps {
	edgeStyleCss: string
	color: string | null
	selectedOutlineColor: string | null
}

/*
These are things for which CRUD operations are valid through Explorer.svelte, as opposed to
ElementType which also includes elements that are CRUD-able from the Flow.svelte or only through
some SideSheet.
*/
export enum FileExplorerElementType {
	DEPENDENCIES = 'DEPENDENCIES',
	ACTIVITY = 'ACTIVITY',
	REPORTING = 'REPORTING',
	FLOW = 'FLOW',
	TOPIC = 'TOPIC',
	OUTPUT_OBJECT = 'OUTPUT_OBJECT',
	REPORTING_PROPERTY = 'REPORTING_PROPERTY',
	EXPLORER_RIGHT_CLICK = 'EXPLORER_RIGHT_CLICK',
	TEST_SUITE = 'TEST_SUITE',
	USE_CASE = 'USE_CASE',
	CONSTRAINT = 'CONSTRAINT',
	FUNCTION_CONTRACT = 'FUNCTION_CONTRACT',
	ADAPTER_EXCEPTION = 'ADAPTER_EXCEPTION',
	ADAPTER_SERVICE = 'ADAPTER_SERVICE',
	CUSTOM_TYPE = 'CUSTOM_TYPE',
	FLOW_EVENTS = 'FLOW_EVENTS',
	LEVER = 'LEVER',
	GSP = 'GSP',
	INSTANCE = 'INSTANCE',
	INSTANCE_ITEM = 'INSTANCE_ITEM',
	REFERENCE_TABLE = 'REFERENCE_TABLE',
	RESOURCE_SCHEMA = 'RESOURCE_SCHEMA'
}

/*
Elements are things for which CRUD operations are valid.
*/
export enum ElementType {
	PROJECT = 'PROJECT',
	ACTIVITY = FileExplorerElementType.ACTIVITY,
	REPORTING = FileExplorerElementType.REPORTING,
	FLOW = FileExplorerElementType.FLOW,
	TOPIC = FileExplorerElementType.TOPIC,
	OUTPUT_OBJECT = FileExplorerElementType.OUTPUT_OBJECT,
	REPORTING_PROPERTY = FileExplorerElementType.REPORTING_PROPERTY,
	TEST_SUITE = FileExplorerElementType.TEST_SUITE,
	USE_CASE = FileExplorerElementType.USE_CASE,
	METADATA = 'METADATA',
	DEPENDENCIES = FileExplorerElementType.DEPENDENCIES,
	CONSTRAINT = FileExplorerElementType.CONSTRAINT,
	FUNCTION_CONTRACT = FileExplorerElementType.FUNCTION_CONTRACT,
	ADAPTER_EXCEPTION = FileExplorerElementType.ADAPTER_EXCEPTION,
	ADAPTER_SERVICE = FileExplorerElementType.ADAPTER_SERVICE,
	FLOW_EVENTS = FileExplorerElementType.FLOW_EVENTS,
	NODE = GraphElementType.NODE,
	EDGE = GraphElementType.EDGE,
	CUSTOM_TYPE = 'CUSTOM_TYPE',
	LEVER = 'LEVER',
	REFERENCE_TABLE = 'REFERENCE_TABLE',
	RESOURCE_SCHEMA = 'RESOURCE_SCHEMA',
	GSP = 'GSP',
	NLU_CONFIG = 'NLU_CONFIG',
	INTENT_SPECTRUM = 'INTENT_SPECTRUM',
	PERSONA = 'PERSONA',
	KNOWLEDGE_COLLECTION = 'KNOWLEDGE_COLLECTION',
	INSTANCE = 'INSTANCE',
	INSTANCE_ITEM = 'INSTANCE_ITEM',
	EXPRESSION = 'EXPRESSION'
}

export enum DetailedSelectItemableElementType {
	TOPIC = FileExplorerElementType.TOPIC,
	FLOW = FileExplorerElementType.FLOW,
	NLU_CONFIG = ElementType.NLU_CONFIG,
	INSTANCE = ElementType.INSTANCE,
	INTENT_SPECTRUM = ElementType.INTENT_SPECTRUM,
	PERSONA = ElementType.PERSONA,
	COMPANY = 'COMPANY',
	LEVER = ElementType.LEVER,
	GSP = ElementType.GSP,
	ADAPTER_SERVICE = ElementType.ADAPTER_SERVICE,
	FUNCTION_CONTRACT = ElementType.FUNCTION_CONTRACT,
	CUSTOM_TYPE = ElementType.CUSTOM_TYPE,
	REFERENCE_TABLE = ElementType.REFERENCE_TABLE,
	NODE = 'NODE',
	KNOWLEDGE_COLLECTION = ElementType.KNOWLEDGE_COLLECTION,
	RELEASE_VERSION = 'RELEASE_VERSION',
	EMBEDDING_MODEL = 'EMBEDDING_MODEL',
	TAG = 'TAG'
}

/** Shared shape for persona / knowledge-collection release rows in version pickers. */
export type ReleaseVersionSelectable = {
	id?: string
	releaseVersion?: string
	versionNumber?: number
	createdBy?: string
	createdAt?: Date
	releaseNotes?: string
	isLatestDraft?: boolean
}

export type EmbeddingModelSelectable = {
	embeddingModelId?: string
	name?: string
	status?: string
	activeSyncId?: string
}

export type DetailedSelectItemableElement =
	| IdAndNamedElement // in this case IdAndNamedElement is used for Topic
	| FlowViewModel
	| Node
	| NluConfig
	| InstanceDescription
	| IntentSpectrum
	| Persona
	| Lever
	| ProjectParameter // Gsp
	| CustomTypeSchema
	| KnowledgeCollectionDTO
	| ReleaseVersionSelectable
	| EmbeddingModelSelectable
	| Tag

export type GlobalElementType =
	| FileExplorerElementType.CONSTRAINT
	| FileExplorerElementType.FUNCTION_CONTRACT
	| FileExplorerElementType.ADAPTER_SERVICE
	| FileExplorerElementType.CUSTOM_TYPE
	| FileExplorerElementType.FLOW_EVENTS
	| FileExplorerElementType.LEVER
	| FileExplorerElementType.GSP
	| FileExplorerElementType.INSTANCE
export const GlobalElementTypes = [
	FileExplorerElementType.CONSTRAINT,
	FileExplorerElementType.FUNCTION_CONTRACT,
	FileExplorerElementType.ADAPTER_SERVICE,
	FileExplorerElementType.CUSTOM_TYPE,
	FileExplorerElementType.FLOW_EVENTS,
	FileExplorerElementType.LEVER,
	FileExplorerElementType.GSP,
	FileExplorerElementType.INSTANCE
] as const
export type GlobalElement =
	| Constraint
	| FunctionContract
	| AdapterService
	| CustomTypeSchema
	| ProjectParameter
	| AbstractResource
	| FlowEvents

export type FlowEvents = {
	id: FileExplorerElementType.FLOW_EVENTS
	flowEvents: Record<FlowEvent, string>
}

/*
Things that are selectable in the Explorer and have a tab representation
This is as opposed to activities that cannot be selected (only opened or closed in the Explorer).
*/
export type TabType =
	| FileExplorerElementType.ACTIVITY
	| FileExplorerElementType.FLOW
	| FileExplorerElementType.TOPIC
	| FileExplorerElementType.USE_CASE
	| FileExplorerElementType.TEST_SUITE
	| ElementType.INSTANCE_ITEM
	| ElementType.INSTANCE
	| FileExplorerElementType.FLOW_EVENTS
	| GlobalElementType

export type TabElement =
	| FlowViewModel
	| Topic
	| Usecase
	| TestSuiteViewModel
	| GlobalElement[]
	| FlowEvents
	| InstanceConfiguration

/*
Which type of Folder.svelte to render for a given folder.
*/
export type ExplorerFolderWithFilesType =
	| FileExplorerElementType.ACTIVITY
	| FileExplorerElementType.TEST_SUITE
	| FileExplorerElementType.REPORTING

export interface GlobalFolderItem {
	type: GlobalElementType
	iconName?: IconName
}

/*
The type of the data object in a given Folder.svelte.
*/
export type FolderElement =
	| ActivityViewModel
	| AbstractReportingElement
	| TestSuiteViewModel
	| GlobalFolderItem

/*
A new element dialog can be created for one of these types.
*/
export type NewElementDialogType =
	| FileExplorerElementType.FLOW
	| FileExplorerElementType.TOPIC
	| FileExplorerElementType.USE_CASE

export type NewElementDialogElement = FlowViewModel | Topic | Usecase

/*
Any possible action that the user takes that modifies the project object can be abstracted as an ElementOperation 
where we have some CRUD Operator and some operand (which is the element being operated on.)
*/
export interface ElementOperation {
	operand: ElementOperand
	operator: ElementOperator
	errors?: Record<string, string>
}

export interface SelectedGraphElement {
	graphElementType: GraphElementType
	id: string
}
export interface SelectGraphElementAction {
	element: SelectedGraphElement | null // null when the selection is cleared
}

export enum ElementOperator {
	CREATE = 'CREATE',
	READ = 'READ',
	UPDATE = 'UPDATE',
	DELETE = 'DELETE'
}

export type ConstructiveOperator = ElementOperator.CREATE | ElementOperator.UPDATE
export interface ElementOperand {
	type: ElementType
	parentElementId: string | null
	element: Element | null
}

export type SideSheetPayload = Node | Edge | Topic | FlowViewModel | Usecase | GlobalElement[]

export interface SidesheetElement {
	parentElementId: string | null
	element: SideSheetPayload | null // null if the element is to be created from scratch
	type: ElementType
}

export interface IdAndNamedElement {
	id: string
	name: string
}

export interface NamedElementBeingRenamed {
	namedElement: NamedElement
	type: FileExplorerElementType
	oldName: string
	newName: string | null
}

export interface EdgeWithItsNodes {
	edge: Edge
	sourceNode: Node
	targetNode: Node
}

export enum EdgeFilter {
	ALL = 'ALL',
	INCOMING = 'INCOMING',
	OUTGOING = 'OUTGOING'
}

/*
Relevant for having the correct types on the 'data' object of nodes.
*/
export type NodeTypeToPersistenceData = {
	[StateType.Start]: PersistenceStartState
	[StateType.End]: PersistenceEndState
	[StateType.Query]: PersistenceQueryState
	[StateType.Prompt]: PersistencePromptState
	[StateType.FunctionCall]: PersistenceFunctionCallState
	// [StateType.SubFlow]: PersistenceSubFlowState;
	[StateType.PrequalificationState]: PersistencePrequalificationState
	[StateType.Faq]: PersistenceFaqState
	[StateType.SlotFilling]: PersistenceSlotFillingState
	[StateType.ReadState]: PersistenceReadState
	// [StateType.Expertise]: PersistenceExpertiseState;
}

export interface FolderRequest {
	name: string
	type: ExplorerFolderWithFilesType
}

export type ValidatableFormComponent = {
	/** Returns errors (including `{}` when valid); `undefined` blocks submission for strict requirements. */
	validate: (isSubmitting: boolean) => FormValidationResult | Promise<FormValidationResult>
}

export type ValidatableFormComponentThatReturnsElementOperation = {
	validateAndGetElementOperation: () => Promise<ElementOperation | null>
}
